import { track } from './analytics';

/**
 * Silently finish an interrupted guided restore on startup.
 *
 * The user consented when they STARTED the restore — `restorePending` +
 * `restorePendingScope` (what was in scope: files, DJ targets, destination)
 * record that intent durably, so re-asking on every launch is friction, not
 * safety. The run is observable the whole time through the app-wide restore
 * toast and the Settings card (both fed by the same progress events), and
 * stoppable from either.
 *
 * Outcomes:
 *   'done'     — resumed and finished (or stopped by the user, which is a
 *                decision, not a failure); markers cleared.
 *   'fallback' — can't run silently (needs a destination folder we never
 *                learned, or nothing actionable) — show the visible prompt.
 *   'failed'   — the resume errored; leave restorePending so the caller can
 *                offer the prompt now and the next launch can try again.
 *
 * Everything here mirrors the wizard's runMissingRestore, minus UI state —
 * including recording lastMergeDj so the wizard's "nothing new since" state
 * stays truthful for merges that happened without it.
 */
export async function autoResumeRestore({ backupApi, scope, lastMergeDj = {}, notify }) {
    const filesInScope = scope?.files !== false; // default true — the guided flow restores files
    const targets = Array.isArray(scope?.targets) ? scope.targets : [];

    let summaryFiles = null;
    let moves = [];
    if (filesInScope) {
        const filesRes = await backupApi.listFiles();
        if (filesRes?.error) throw new Error(filesRes.error);
        const items = (filesRes?.files || []).map((f) => ({
            sha256: f.sha256, filePath: f.filePath, fileName: f.fileName, sizeBytes: f.sizeBytes,
        }));
        if (items.length) {
            const res = await backupApi.restoreFiles({
                items,
                destDir: scope?.destDir || undefined,
                destDirMode: scope?.destDir ? (scope?.destDirMode || 'orphans') : undefined,
            });
            if (res?.error) throw new Error(res.error);
            // Orphans with no remembered folder — silent resume can't choose
            // one for the user. Hand back to the visible prompt.
            if (res?.needsDestDir) return 'fallback';
            summaryFiles = res;
            moves = res?.moves || [];
            if (res?.restored?.length) {
                track('backup_file_restored', { count: res.restored.length, from: 'auto_resume' });
            }
            if (res?.stopped) {
                // The user stopped it mid-resume (Settings card / wizard) —
                // that decision wins, same as stopping the original run.
                await backupApi.setEnabled({
                    restorePending: false, restorePendingScope: null, restoreAutoAttempts: 0,
                    restorePromptDismissed: true,
                }).catch(() => {});
                return 'done';
            }
        }
    }

    // The MixGoat-library merge rides along here too (same as the wizard):
    // enriched track rows + playlists, ADD-ONLY. Best-effort — a resume must
    // finish the files/DJ work even when the library merge can't run (older
    // backup, older preload); the next visible Restore reports library issues.
    // Scopes recorded before the library flag existed (undefined) ride along;
    // only an explicit opt-out (the user unticked the row) skips it.
    if (scope?.library !== false && backupApi.restoreMergeLibrary) {
        try {
            const libRes = await backupApi.restoreMergeLibrary({ pathMoves: moves });
            if (libRes && !libRes.error && !libRes.unsupported) {
                track('backup_library_restored', {
                    tracks_added: libRes.tracksAdded || 0,
                    tracks_enriched: libRes.tracksEnriched || 0,
                    playlists_created: libRes.playlistsCreated || 0,
                    playlist_tracks_added: libRes.playlistTracksAdded || 0,
                    from: 'auto_resume',
                });
            }
        } catch { /* best-effort */ }
    }

    let summaryDj = null;
    if (targets.length) {
        const res = await backupApi.restoreMergeDj({ targets, pathMoves: moves });
        if (res?.error) throw new Error(res.error);
        summaryDj = res;
        // Same bookkeeping as the wizard: which snapshot each target merged,
        // and how much music was still missing at that moment.
        try {
            const missingAfter = summaryFiles ? (summaryFiles.failed || []).length : 0;
            const merged = { ...lastMergeDj };
            for (const t of targets) {
                merged[t] = { snapshotId: res.snapshotId || null, at: Date.now(), filesMissing: missingAfter };
            }
            await backupApi.setEnabled({ lastMergeDj: merged }).catch(() => {});
        } catch { /* bookkeeping only */ }
    }

    await backupApi.setEnabled({
        restorePending: false, restorePendingScope: null, restoreAutoAttempts: 0,
        restorePromptDismissed: true,
    }).catch(() => {});

    track('backup_restore_missing_completed', {
        auto: true,
        files_restored: summaryFiles?.restored?.length || 0,
        files_failed: summaryFiles?.failed?.length || 0,
        dj_targets: targets.length,
        empty_targets: (summaryDj?.targets || []).filter((t) => t.nothingToMerge).length,
    });

    if (notify) {
        const bits = [];
        if (summaryFiles) {
            bits.push(`${(summaryFiles.restored || []).length.toLocaleString()} files restored`);
            if ((summaryFiles.failed || []).length) bits.push(`${summaryFiles.failed.length.toLocaleString()} failed`);
        }
        if (summaryDj?.targets?.length) bits.push('DJ apps updated');
        notify('Your restore is finished', bits.join(' · ') || 'Everything was already on this computer.');
    }
    return 'done';
}
